/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Badge,
  Button,
  Col,
  Input,
  Row,
  Segmented,
  notification,
  Spin,
  Empty,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/layout/header";
import Styles from "./op.module.scss";
import Icon from "@ant-design/icons";
import { CustomSpin, FilterIcon, SearchIcon } from "../../assets/icons/icons";
import UniversalScanButton from "../../components/mobile/UniversalScanButton";
import FilterModal from "../../components/mobile/FilterModal";
import { api } from "../../services/api";
import ListingCards from "../../components/reusable/listingCards";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import debounce from "lodash/debounce";
import { useAllTotes, useAllTrolleys, useLocations } from "../common/queries";
import type { FilterValues } from "../../components/mobile/types";
import type { Tote, Trolley } from "../../services/types";
import { hasActiveFilters } from "../../utils/filterUtils";
import { getUserTimeZone } from "../../utils/timeUtils";
import CONSTANTS from "../../constants/constants";

interface ListingItem {
  toteNo: string | number;
  status: string;
  totalItems: number;
  priority: boolean;
  id?: string;
  fullData?: unknown;
  registeredDate?: string;
}

const ITEMS_PER_PAGE = 10;
const SCROLL_THRESHOLD = 300;
const OPERATOR_TAB_STORAGE_KEY = "operatorActiveTab";
const OPERATOR_FILTERS_KEY = "operatorFilters";
const OPERATOR_SEARCH_QUERY_KEY = "operatorSearchQuery";
const EMPTY_TOTE_LIST: Tote[] = [];
const EMPTY_TROLLEY_LIST: Trolley[] = [];

// Helper function to load filters from sessionStorage
const loadFiltersFromStorage = (key: string): FilterValues | null => {
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error(`Error loading filters from ${key}:`, error);
  }
  return null;
};

// Helper function to save filters to sessionStorage
const saveFiltersToStorage = (key: string, filters: FilterValues | null) => {
  try {
    if (filters && hasActiveFilters(filters)) {
      sessionStorage.setItem(key, JSON.stringify(filters));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error saving filters to ${key}:`, error);
  }
};

const OpLanding = () => {
  // state
  // Initialize segment from session storage or default to "Tote"
  const [segment, setSegment] = useState(() => {
    const savedTab = sessionStorage.getItem(OPERATOR_TAB_STORAGE_KEY);
    return savedTab || "Tote";
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Separate filter states for totes and trolleys to prevent cross-contamination
  const [toteFilters, setToteFilters] = useState<FilterValues | null>(() => {
    return loadFiltersFromStorage(`${OPERATOR_FILTERS_KEY}_tote`);
  });
  const [trolleyFilters, setTrolleyFilters] = useState<FilterValues | null>(() => {
    return loadFiltersFromStorage(`${OPERATOR_FILTERS_KEY}_trolley`);
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem(OPERATOR_SEARCH_QUERY_KEY) || "";
  });
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [displayedItems, setDisplayedItems] = useState<ListingItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isCheckingTote, setIsCheckingTote] = useState(false);
  const [isCheckingTrolley, setIsCheckingTrolley] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Create debounced function using lodash - only create once using useMemo
  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchQuery(value);
      }, 1000),
    []
  );

  // Set initial debounced value on mount only
  useEffect(() => {
    setDebouncedSearchQuery(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update debounced search when searchQuery changes
  useEffect(() => {
    debouncedUpdateSearch(searchQuery);

    // Cleanup: cancel pending debounced calls
    return () => {
      debouncedUpdateSearch.cancel();
    };
  }, [searchQuery, debouncedUpdateSearch]);

  // Memoize query params to prevent infinite re-renders
  const toteQueryParams = useMemo(() => {
    const params: any = {
      page: 1,
      page_size: 100,
    };
    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    if (toteFilters?.from_date) params.from_date = toteFilters.from_date;
    if (toteFilters?.to_date) params.to_date = toteFilters.to_date;
    if (toteFilters?.location_id) params.location_id = toteFilters.location_id;
    if (toteFilters?.status_ids && toteFilters.status_ids.length > 0) {
      params.status_ids = toteFilters.status_ids;
    }
    if (toteFilters?.priority_ids && toteFilters.priority_ids.length > 0) {
      params.priority_ids = toteFilters.priority_ids;
    }
    return params;
  }, [
    debouncedSearchQuery,
    toteFilters?.from_date,
    toteFilters?.to_date,
    toteFilters?.location_id,
    toteFilters?.status_ids,
    toteFilters?.priority_ids,
  ]);

  const trolleyQueryParams = useMemo(() => {
    const params: any = {
      page: 1,
      page_size: 100,
    };
    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    if (trolleyFilters?.from_date) params.from_date = trolleyFilters.from_date;
    if (trolleyFilters?.to_date) params.to_date = trolleyFilters.to_date;
    if (trolleyFilters?.location_id) params.location_id = trolleyFilters.location_id;
    if (trolleyFilters?.status_ids && trolleyFilters.status_ids.length > 0) {
      params.status_ids = trolleyFilters.status_ids;
    }
    if (trolleyFilters?.priority_flag !== undefined) {
      params.priority_flag = trolleyFilters.priority_flag;
    }
    return params;
  }, [
    debouncedSearchQuery,
    trolleyFilters?.from_date,
    trolleyFilters?.to_date,
    trolleyFilters?.location_id,
    trolleyFilters?.status_ids,
    trolleyFilters?.priority_flag,
  ]);

  // React Query hooks - Pass search query and filters to API when available
  const {
    data: totesData,
    isLoading: l1,
    error: e1,
    refetch: refetchTotes,
  } = useAllTotes(toteQueryParams, true);

  const {
    data: trolleysData,
    isLoading: l2,
    error: e2,
    refetch: refetchTrolleys,
  } = useAllTrolleys(trolleyQueryParams, true);

  const totes = totesData ?? EMPTY_TOTE_LIST;
  const trolleys = trolleysData ?? EMPTY_TROLLEY_LIST;
  const { data: locationsData } = useLocations();

  const getAvailableTotesCount = useCallback(
    (trolley: Trolley): number => {
      if (trolley.availableTotes !== undefined) {
        return trolley.availableTotes;
      }
      return totes?.filter((tote) => tote.trolleyId === trolley.id).length;
    },
    [totes]
  );

  // Transform data for listing view
  const transformData = useCallback((): ListingItem[] => {
    if (segment === "Tote") {
      return totes?.map((tote) => ({
        toteNo: tote.toteNo,
        status: tote.activityStatus,
        totalItems: parseInt(tote.itemCount) || 0,
        priority: tote.priority,
        id: tote.id,
        fullData: tote,
        registeredDate: tote.registeredDate,
      }));
    }

    return trolleys?.map((trolley) => ({
      toteNo: trolley.id,
      status: trolley.status,
      totalItems: getAvailableTotesCount(trolley),
      priority: trolley.priority,
      id: trolley.id,
      fullData: trolley,
      registeredDate: trolley.registeredDate,
    }));
  }, [segment, totes, trolleys, getAvailableTotesCount]);

  // Apply filters and search (API handles filtering for both totes and trolleys now)
  const applyFiltersAndSearch = useCallback(
    (data: ListingItem[]): ListingItem[] => {
      // For both totes and trolleys, API handles filtering, so just return data as-is
      // Client-side filtering is no longer needed
      return data;
    },
    []
  );

  // Persist segment changes to session storage
  useEffect(() => {
    sessionStorage.setItem(OPERATOR_TAB_STORAGE_KEY, segment);
  }, [segment]);

  // Persist search query to sessionStorage
  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem(OPERATOR_SEARCH_QUERY_KEY, searchQuery);
    } else {
      sessionStorage.removeItem(OPERATOR_SEARCH_QUERY_KEY);
    }
  }, [searchQuery]);

  // Refetch list API when tab changes
  useEffect(() => {
    if (segment === "Tote") {
      refetchTotes();
    } else {
      refetchTrolleys();
    }
  }, [segment, refetchTotes, refetchTrolleys]);

  useEffect(() => {
    const allData = transformData();
    const filteredData = applyFiltersAndSearch(allData);

    setDisplayedItems(filteredData?.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredData?.length > ITEMS_PER_PAGE);
  }, [segment, totes, trolleys]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    // Simulate API delay
    setTimeout(() => {
      const allData = transformData();
      const filteredData = applyFiltersAndSearch(allData);

      const nextPageStart = displayedItems?.length;
      const nextPageEnd = nextPageStart + ITEMS_PER_PAGE;
      const nextItems = filteredData.slice(nextPageStart, nextPageEnd);

      if (nextItems.length > 0) {
        setDisplayedItems((prev) => [...prev, ...nextItems]);
        setHasMore(nextPageEnd < filteredData.length);
      } else {
        setHasMore(false);
      }

      setIsLoadingMore(false);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayedItems?.length,
    isLoadingMore,
    hasMore,
    segment,
    totes,
    trolleys,
  ]);

  // Handle scroll for infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD && hasMore && !isLoadingMore) {
      loadMoreItems();
    }
  }, [hasMore, isLoadingMore, loadMoreItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Always refetch lists when component mounts and is on landing page
  const isInitialMount = useRef<boolean>(true);
  
  useEffect(() => {
    const isOnLandingPage = location.pathname === CONSTANTS.PAGE_ROUTES.OP_LANDING;
    
    // Skip refetch only on very first app load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Still refetch on first mount if we're on landing page (handles direct navigation)
      if (isOnLandingPage) {
        refetchTotes();
        refetchTrolleys();
      }
      return;
    }
    
    // Always refetch lists whenever we're on the landing page
    if (isOnLandingPage) {
      console.log('Refetching lists on landing page');
      refetchTotes();
      refetchTrolleys();
    }
  }, [location.pathname, refetchTotes, refetchTrolleys]);

  // Error handling
  useEffect(() => {
    if (e1 || e2) {
      notification.error({
        message: "Failed to fetch the Trolley list! Try again later",
        description:
          segment === "Tote"
            ? "Failed to load tote list. Please try again later."
            : "Failed to load trolley list. Please try again later.",
        duration: 5,
      });
    }
  }, [e1, e2, segment]);

  const handleToteScanned = async (code: string) => {
    setIsCheckingTote(true);
    try {
      // Use checkTote API to verify tote exists
      const response = await api.checkTote({
        tote_id: code,
      });

      // Check if tote is registered (has tote_details in response)
      if (
        response.status_code === 200 &&
        (response.tote_details?.tote_id || response.tote_details?.id)
      ) {
        // Extract the record ID from checkTote response
        const toteRecordId = response.tote_details?.id;
        // Use record ID for navigation, fallback to scanned code if record ID not available
        const toteIdForNavigation = toteRecordId ? String(toteRecordId) : code;

        // Check tote status
        const toteStatus = response.tote_details?.tote_status?.toLowerCase() || "";

        // If tote status is "Completed", navigate to registration page
        if (toteStatus === "completed") {
          setIsCheckingTote(false);
          setTimeout(() => {
            navigate(`/register-tote-error?toteId=${encodeURIComponent(code)}`);
          }, 100);
          return;
        }

        // For other statuses (Available, Loaded to Trolley, Unloaded from Trolley), navigate to tote info page
        // Use record ID from checkTote response
        setIsCheckingTote(false);
        navigate(`/tote-info/${encodeURIComponent(toteIdForNavigation)}`);
      } else {
        // Tote not registered - navigate to register flow
        setIsCheckingTote(false);
        notification.info({
          message: "Tote Not Found",
          description: `Tote ID: ${code} is not registered in our system`,
        });
        setTimeout(() => {
          navigate(`/register-tote-error?toteId=${encodeURIComponent(code)}`);
        }, 1000);
      }
    } catch (error: any) {
      // API error - tote not found, navigate to registration flow
      console.log("Tote check failed, navigating to registration:", error);
      setIsCheckingTote(false);
      notification.info({
        message: "Tote Not Found",
        description: `Tote ID: ${code} is not registered in our system`,
      });
      setTimeout(() => {
        navigate(`/register-tote-error?toteId=${encodeURIComponent(code)}`);
      }, 1000);
    }
  };

  const handleTrolleyScanned = async (code: string) => {
    console.log("Trolley scanned:", code);
    setIsCheckingTrolley(true);
    try {
      // Use checkTrolley API to verify trolley exists
      const response = await api.checkTrolley({
        trolley_id: code,
      });

      const hasTrolleyDetails = Boolean(
        response?.trolley_details?.trolley_id || response?.trolley_details?.id
      );

      // Check message to determine if trolley is registered
      // If message indicates trolley isn't registered or trolley details missing, navigate to register page
      if (
        (response.message && response.message.includes("isn't registered")) ||
        !hasTrolleyDetails
      ) {
        setIsCheckingTrolley(false);
        notification.info({
          message: "Trolley Not Found",
          description:
            response?.message ||
            `Trolley ID: ${code} is not registered in our system`,
        });
        setTimeout(() => {
          navigate(`/register-trolley-error?trolleyId=${encodeURIComponent(code)}`);
        }, 1000);
        return;
      }

      // Trolley exists - navigate to trolley info page
      // Extract the trolley_id string from checkTrolley response (not the numeric record ID)
      const trolleyIdString = response?.trolley_details?.trolley_id || code;
      
      setIsCheckingTrolley(false);
      // Encode the ID to handle special characters like forward slashes
      // Use trolley_id string for navigation (e.g., "TROL23", not the numeric record ID)
      navigate(`/trolley-info/${encodeURIComponent(trolleyIdString)}`);
    } catch (error: any) {
      // API error (404 or other) - trolley not found, navigate to registration flow
      console.log("Trolley check failed, navigating to registration:", error);
      setIsCheckingTrolley(false);
      notification.info({
        message: "Trolley Not Found",
        description: `Trolley ID: ${code} is not registered in our system`,
      });
      setTimeout(() => {
        navigate(`/register-trolley-error?trolleyId=${encodeURIComponent(code)}`);
      }, 1000);
    }
  };

  const handleLocationScanned = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      return;
    }

    // Check if location exists in the locations list
    const normalizedCode = trimmed.toLowerCase();
    const matchedLocation =
      locationsData?.locations?.find(
        (loc) => loc.location_barcode?.toLowerCase() === normalizedCode
      ) ||
      locationsData?.locations?.find(
        (loc) => loc.primary_location?.toLowerCase() === normalizedCode
      );

    if (!matchedLocation) {
      // Location not found - show info message and stay on current page
      notification.info({
        message: "Location Not Found",
        description: `Scanned location "${trimmed}" is not available in the system`,
      });
      return;
    }

    // Location found - navigate to location info page
    navigate(`/location-info/${encodeURIComponent(trimmed)}`);
  };

  const handleFilterApply = useCallback((newFilters: FilterValues) => {
    const filtersToSet = hasActiveFilters(newFilters) ? newFilters : null;
    if (segment === "Tote") {
      setToteFilters(filtersToSet);
      saveFiltersToStorage(`${OPERATOR_FILTERS_KEY}_tote`, filtersToSet);
    } else {
      setTrolleyFilters(filtersToSet);
      saveFiltersToStorage(`${OPERATOR_FILTERS_KEY}_trolley`, filtersToSet);
    }
  }, [segment]);

  const handleCardClick = (item: ListingItem) => {
    if (segment === "Tote") {
      // Use id (record ID) instead of toteNo for API compatibility
      const toteId = item.id || String(item.toteNo);
      navigate(`/tote-info/${encodeURIComponent(String(toteId))}`);
    } else {
      // Encode the ID to handle special characters like forward slashes
      navigate(`/trolley-info/${encodeURIComponent(item.id || item.toteNo)}`);
    }
  };

  const isSegmentLoading = segment === "Tote" ? l1 : l2;
  const currentFilters = segment === "Tote" ? toteFilters : trolleyFilters;
  const isFilterApplied = hasActiveFilters(currentFilters);

  const opItems = [
    {
      label: <div>Tote ({totes?.length})</div>,
      value: "Tote",
      key: "1",
    },
    {
      label: <div>Trolley ({trolleys?.length})</div>,
      value: "Trolley",
      key: "2",
    },
  ];

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.listingContainer}>
        <Row align="middle" justify="space-between">
          <Col span={20}>
            <Input
              prefix={<Icon component={SearchIcon} />}
              placeholder={
                segment === "Tote"
                  ? "Search Tote"
                  : "Search Trolley"
              }
              style={{ width: "100%" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              enterKeyHint="search"
              onPressEnter={(e) => {
                // Trigger search when search button is pressed
                // The debounced search will handle the actual search
                debouncedUpdateSearch(searchQuery);
                // Close the keyboard
                e.currentTarget.blur();
              }}
            />
          </Col>
          <Col span={3}>
            <Badge dot={isFilterApplied} offset={[-2, 2]}>
              <Button
                icon={<Icon component={FilterIcon} />}
                onClick={() => setIsFilterOpen(true)}
              />
            </Badge>
          </Col>
        </Row>

        <Row justify="center" className={Styles.spacingRow}>
          <Segmented
            options={opItems}
            className="mainSegments"
            value={segment}
            onChange={(value) => {
              setSegment(value);
              // Keep filters and search query when switching tabs (persist context)
              // Only clear search query, filters remain applied
              setSearchQuery("");
            }}
          />
        </Row>

        {isSegmentLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin indicator={<CustomSpin />} />
          </div>
        ) : displayedItems.length > 0 ? (
          <div
            ref={scrollContainerRef}
            className={Styles.spacingRow}
            style={{
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {displayedItems?.map((item, index) => (
              <ListingCards
                key={`${item.id || item.toteNo}-${index}`}
                details={item}
                listingType={segment}
                onClick={() => handleCardClick(item)}
              />
            ))}
            {isLoadingMore && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin indicator={<CustomSpin />} />
              </div>
            )}
            {!hasMore && displayedItems.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#999",
                  fontSize: "14px",
                }}
              >
                No more items to load
              </div>
            )}
          </div>
        ) : (
          <Empty
            description={
              segment === "Tote" ? "No totes found" : "No trolleys found"
            }
          />
        )}
      </div>
      <UniversalScanButton
        onToteScanned={handleToteScanned}
        onTrolleyScanned={handleTrolleyScanned}
        onLocationScanned={handleLocationScanned}
        isCheckingTote={isCheckingTote}
        isCheckingTrolley={isCheckingTrolley}
      />
      <FilterModal
        key={segment}
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
        filterType={segment.toLowerCase() as "tote" | "trolley"}
        initialValues={currentFilters || undefined}
      />
    </div>
  );
};

export default OpLanding;
